import { useState } from "react";

import LoadingButton from "@mui/lab/LoadingButton";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { SxProps } from "@mui/material/styles";

import Dialog from "components/dialog";

import { displayNumber } from "../call-display.util";

type RemoveFromPatientDialogProps = {
  patientName: string;
  // Who the call shows once the patient is off it: the Rolodex contact, else
  // the number.
  callerName: string;
  remoteNumber: string;
  // The number sits in the patient's secondary phone ("Remember number" put
  // it there), so the dialog offers to take it off too.
  numberRemembered: boolean;
  onClose: () => void;
  // Resolves null on success (the host closes the dialog) or the error response.
  onRemove: (forgetNumber: boolean) => Promise<{ error: unknown } | null>;
  // Root styles (the softphone lifts its dialogs over the panel).
  sx?: SxProps;
};

// Take a patient staff attached by hand off a call (ENG-2563): the call and
// its voicemail leave the patient's record, the phone note and inbox items the
// attach filed are deleted, and the call names its caller again.
export default function RemoveFromPatientDialog({
  patientName,
  callerName,
  remoteNumber,
  numberRemembered,
  onClose,
  onRemove,
  sx
}: RemoveFromPatientDialogProps) {
  const [forget, setForget] = useState(numberRemembered);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleRemove = async () => {
    setIsSubmitting(true);
    const error = await onRemove(numberRemembered && forget);
    setIsSubmitting(false);
    if (!error) onClose();
  };

  return (
    <Dialog
      title="Remove this call from the patient?"
      subtitle={`Call with ${callerName}`}
      open
      onClose={onClose}
      maxWidth="sm"
      sx={sx}
      actions={
        <>
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            color="error"
            loading={isSubmitting}
            onClick={handleRemove}
            data-testid="remove-from-patient-submit"
          >
            Remove from patient
          </LoadingButton>
        </>
      }
    >
      <Stack spacing={2} sx={{ pb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {`The call and its voicemail come off ${patientName}'s record, and the phone note and inbox items filed from it are deleted. The call shows ${callerName} again.`}
        </Typography>
        {numberRemembered && (
          <Stack spacing={0}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={forget}
                  onChange={(event) => setForget(event.target.checked)}
                  inputProps={
                    { "data-testid": "remove-from-patient-forget" } as never
                  }
                />
              }
              label={`Also remove ${displayNumber(remoteNumber)} from ${patientName}'s record`}
            />
            <FormHelperText sx={{ ml: 4, mt: -0.5 }}>
              {`It was saved as their secondary phone when the call was attached. Until it is removed, every call from this number matches ${patientName}.`}
            </FormHelperText>
          </Stack>
        )}
      </Stack>
    </Dialog>
  );
}
